import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Button, ButtonGroup, Flex, Heading, ModalDialog, Text, TextButton } from '@zillow/constellation';
import LegalComplianceWallBodySection from './BodySection';

import { IcraaComplianceContext } from '../../../../../../contexts/IcraaComplianceContext';
import { getAllRequiredFormsAnswered } from './IcraaComplianceWallUtil';
import { useSelector } from 'react-redux';
import useQueryVariables from '../../../../../../hooks/useQueryVariables';
import { useMutation } from '@apollo/client';
import { ACCEPT_ICRAA_MUTATION, GET_ICRAA_PAGE_CONFIG_QUERY } from '../../../../../../utils/complianceMessaging';
import { listingAliasSelector, userIdIreneSelector } from '../../../../../../selectors';
import IcraaComplianceWallError from './IcraaComplianceWallError';

export const StyledModalDialog = styled(ModalDialog)`
  & #legal-compliance-wall-modal {
    max-width: 559px;
  }
`;

export default function IcraaComplianceWall({ applicant, onCancelCleanupScreening }) {
  const { applicationId } = useQueryVariables();
  const listingAlias = useSelector(listingAliasSelector);
  const zuidIrene = useSelector(userIdIreneSelector);
  const { userId: applicantId } = applicant;
  const { isLoading, isOpen, setIsOpen, icraaPageConfig, onCancelCleanup } = useContext(IcraaComplianceContext);
  const [formAnswers, setFormAnswers] = useState({});
  function onClose() {
    setIsOpen(false);
    setFormAnswers({});
  }
  function onCancel() {
    onClose();
    onCancelCleanup();
    onCancelCleanupScreening();
  }
  const [acceptIcraaMutation, { error }] = useMutation(ACCEPT_ICRAA_MUTATION, {
    variables: {
      zuidIrene,
      zuids: [applicantId],
      listingAlias,
      complianceType: icraaPageConfig?.complianceType,
      applicationId,
      ...formAnswers,
    },
    onCompleted: () => {
      onClose();
    },
    refetchQueries: [
      {
        query: GET_ICRAA_PAGE_CONFIG_QUERY,
        variables: {
          applicationId,
          propertyId: listingAlias,
        },
      },
    ],
  });

  if (isLoading || !icraaPageConfig) {
    return null;
  }

  function setFormAnswer(formId, answer) {
    setFormAnswers({
      ...formAnswers,
      [formId]: answer,
    });
  }

  const { header, body, callToActionLabel, finePrint, requiredForms } = icraaPageConfig;

  const isAcceptButtonDisabled =
    isLoading || (requiredForms && !getAllRequiredFormsAnswered({ requiredForms, formAnswers }));

  return (
    <StyledModalDialog
      wide
      {...{
        id: 'legal-compliance-wall-modal',
        mediaQuery: {
          xs: { fullScreen: true },
          md: { fullScreen: false },
        },
        isOpen,
        onClose: onCancel,
        ...(Boolean(header) && {
          header: <Heading level={3}>{header}</Heading>,
        }),
        body: (
          <>
            {error && <IcraaComplianceWallError {...error} marginBottom="xs" />}
            {body.map((section = {}, index) => (
              <LegalComplianceWallBodySection key={index} setFormAnswer={setFormAnswer} {...section} />
            ))}
          </>
        ),
        footer: (
          <Flex display="flex" flexDirection="column">
            <ButtonGroup aria-label="Legal compliance actions">
              <TextButton onClick={onCancel}>Cancel</TextButton>
              <Button
                buttonType="primary"
                onClick={acceptIcraaMutation}
                disabled={isAcceptButtonDisabled}
                id="legal-compliance-accept-button"
              >
                {callToActionLabel}
              </Button>
            </ButtonGroup>
            {Boolean(finePrint) && (
              <Text marginTop="xs" fontType="finePrint">
                {finePrint}
              </Text>
            )}
          </Flex>
        ),
      }}
    />
  );
}

IcraaComplianceWall.displayName = 'IcraaComplianceWall';

IcraaComplianceWall.propTypes = {
  applicant: PropTypes.shape({
    userId: PropTypes.string.isRequired,
  }).isRequired,
  onCancelCleanupScreening: PropTypes.func,
};
